// Draw or change a zone: its outline on the map, an optional suggested route,
// and the details printed on the route sheet. "Find streets" asks
// OpenStreetMap (through the server) for the named streets inside the outline
// and a rough count of homes, to size the zone to a bundle of leaflets.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Textarea, Checkbox } from '../lib/ui';
import { useLeafletMap, fitPoints } from '../lib/leafletMap';
import { formatDay } from '../lib/trade';
import {
  OUTLINE_COLOUR, ROUTE_COLOUR, FREE_COLOUR, polygonsOverlap, areaKm2, formatCount,
} from '../lib/leaflets';
import { POSTCODE_DATA, PRIORITY_CONFIG } from './FlierTargeting';
import { ShapeEditor, ZonesLayer } from './LeafletsCommon';
import { Label, errorText } from './TradeCommon';

const MODES = [
  { key: 'outline', label: 'Outline' },
  { key: 'route',   label: 'Route' },
  { key: 'look',    label: 'Just look' },
];

const HINTS = {
  drawOutline: 'Click the map to put corners around the streets. Click the first corner (the round one) to close the outline.',
  outline: 'Drag a corner to move it, drag a small dot to add a corner, right-click a corner to remove it.',
  route: 'Optional: click along the streets in the order to walk them. Drag points to adjust, right-click to remove one.',
  look: 'Move and zoom the map without changing anything.',
};

const streetList = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// The server answers within ~20s with the result or { status: 'running', id };
// a running lookup is asked about every few seconds (Overpass can take minutes).
async function surveyOutline(polygon, stillHere) {
  let { data } = await api.post('/leaflets/admin/survey', { polygon });
  const started = Date.now();
  while (data.status === 'running' && stillHere()) {
    if (Date.now() - started > 4 * 60 * 1000) throw new Error('OpenStreetMap is taking too long to answer. Try again later.');
    await sleep(4000);
    ({ data } = await api.get(`/leaflets/admin/survey/${data.id}`));
  }
  if (data.status === 'failed') throw new Error(data.error);
  return data.result;
}

function SectorCircles({ map }) {
  useEffect(() => {
    if (!map) return undefined;
    const L = window.L;
    const group = L.layerGroup().addTo(map);
    for (const s of POSTCODE_DATA) {
      L.circle([s.lat, s.lng], {
        radius: 1100, color: '#5e7265', weight: 1, dashArray: '2 4', fillOpacity: 0.04, interactive: false,
      }).bindTooltip(`${s.sector} ${s.label}: ${PRIORITY_CONFIG[s.priority].label}`, {
        permanent: true, direction: 'center', className: 'lf-map-label is-sector', interactive: false,
      }).addTo(group);
    }
    return () => group.remove();
  }, [map]);
  return null;
}

export default function LeafletsZoneEditor({ zone, data, onClose, onSaved, onDeleted, onDirty }) {
  const adding = !zone;
  const [form, setForm] = useState(() => ({
    name: zone?.name || '',
    area: zone?.area || '',
    homes: zone?.homes ?? '',
    homesSource: zone?.homesSource || '',
    surveyedAt: zone?.surveyedAt || null,
    streets: (zone?.streets || []).join('\n'),
    instructions: zone?.instructions || '',
    notes: zone?.notes || '',
    archived: !!zone?.archived,
  }));
  const [shape, setShape] = useState(() => ({ outline: zone?.polygon || [], closed: !!zone, route: zone?.route || [] }));
  const [history, setHistory] = useState([]);
  const [mode, setMode] = useState(adding ? 'outline' : 'look');
  const [sectors, setSectors] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [found, setFound] = useState(null); // last OpenStreetMap answer, for the summary line

  const mapRef = useRef(null);
  const { map, error: mapError } = useLeafletMap(mapRef, { doubleClickZoom: false });

  // So the page can ask before a tab change throws the drawing away
  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v, ...(k === 'homes' ? { homesSource: 'manual' } : {}) }));
    setDirty(true);
  };

  const changeShape = (patch) => {
    setHistory(h => [...h.slice(-60), shape]);
    setShape(s => ({ ...s, ...patch }));
    setDirty(true);
  };
  const undo = () => {
    if (!history.length) return;
    setShape(history[history.length - 1]);
    setHistory(h => h.slice(0, -1));
  };

  // The other zones, faint, so a new one can be drawn up against them
  const others = useMemo(() => data.zones.filter(z => z._id !== zone?._id && !z.archived), [data.zones, zone]);
  const otherItems = useMemo(() => others.map(z => ({ zone: z, colour: FREE_COLOUR, fill: 0.06, dashed: true, label: z.name, faded: true })), [others]);
  const overlaps = shape.closed ? others.filter(z => polygonsOverlap(shape.outline, z.polygon)) : [];

  // Start on the zone being changed, or on the other zones when drawing a new one
  const framed = useRef(false);
  useEffect(() => {
    if (!map || framed.current) return;
    framed.current = true;
    if (zone) fitPoints(map, [...zone.polygon, ...(zone.route || [])]);
    else if (others.length) fitPoints(map, others.flatMap(z => z.polygon), 14);
  }, [map, zone, others]);

  const jobsHere = zone ? data.jobs.filter(j => j.zone === zone._id) : [];
  const streets = streetList(form.streets);
  const ticked = (zone?.streetsDone || []).filter(s => streets.includes(s.name)).length;

  // Whether the editor is still open, so a slow lookup stops asking once it isn't.
  // Set in the effect itself too: in development React mounts components twice.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const findStreets = async () => {
    if (!shape.closed) return setError('Close the outline first');
    if (streets.length && !window.confirm('Replace the street list with the streets OpenStreetMap has inside the outline?')) return;
    setBusy('survey');
    setError('');
    try {
      const res = await surveyOutline(shape.outline, () => mounted.current);
      if (!mounted.current || !res) return;
      setFound(res);
      setForm(f => ({
        ...f, streets: res.streets.join('\n'), homes: res.homes, homesSource: 'osm', surveyedAt: new Date().toISOString(),
      }));
      setDirty(true);
    } catch (e) {
      if (mounted.current) setError(errorText(e));
    } finally {
      if (mounted.current) setBusy('');
    }
  };

  const save = async () => {
    if (!form.name.trim()) return setError('Give the zone a name');
    if (!shape.closed || shape.outline.length < 3) return setError('Draw the outline and close it by clicking its first corner');
    if (shape.route.length === 1) return setError('A route needs at least two points (or clear it)');
    setBusy('save');
    setError('');
    const body = {
      name: form.name,
      area: form.area,
      polygon: shape.outline,
      route: shape.route,
      homes: form.homes,
      homesSource: form.homes === '' ? '' : form.homesSource || 'manual',
      surveyedAt: form.surveyedAt,
      streets,
      instructions: form.instructions,
      notes: form.notes,
      archived: form.archived,
    };
    try {
      const { data: res } = adding
        ? await api.post('/leaflets/admin/zones', body)
        : await api.patch(`/leaflets/admin/zones/${zone._id}`, body);
      setDirty(false);
      onSaved(res.zone, adding);
    } catch (e) {
      setError(errorText(e));
      setBusy('');
    }
  };

  const clearTicks = async () => {
    if (!window.confirm('Clear the ticks so the zone can be delivered again from the start?')) return;
    try {
      const { data: res } = await api.post(`/leaflets/admin/zones/${zone._id}/streets`, { clear: true });
      onSaved(res.zone, false, { stay: true });
    } catch (e) {
      setError(errorText(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${zone.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/leaflets/admin/zones/${zone._id}`);
      onDeleted(zone._id);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const close = () => {
    if (dirty && !window.confirm('Discard your changes to this zone?')) return;
    onClose();
  };

  const hint = mode === 'outline' ? (shape.closed ? HINTS.outline : HINTS.drawOutline) : HINTS[mode];

  return (
    <div className="lf-editor">
      <div className="lf-editor__map">
        <div className="lf-toolbar">
          <div className="trade-seg">
            {MODES.map(m => (
              <button key={m.key} type="button" className={mode === m.key ? 'is-on' : ''} onClick={() => setMode(m.key)}>{m.label}</button>
            ))}
          </div>
          <Button size="sm" variant="muted" onClick={undo} disabled={!history.length}>Undo</Button>
          {mode === 'outline' && shape.outline.length > 0 && (
            <Button size="sm" variant="muted" onClick={() => changeShape({ outline: [], closed: false })}>Clear outline</Button>
          )}
          {mode === 'outline' && !shape.closed && shape.outline.length >= 3 && (
            <Button size="sm" variant="primary" onClick={() => changeShape({ closed: true })}>Close outline</Button>
          )}
          {mode === 'route' && shape.route.length > 0 && (
            <Button size="sm" variant="muted" onClick={() => changeShape({ route: [] })}>Clear route</Button>
          )}
          <Checkbox checked={sectors} onChange={e => setSectors(e.target.checked)} label="Targeting sectors" style={{ marginLeft: 'auto', fontSize: 13 }} />
        </div>
        <div className="lf-hint">{hint}</div>
        <div className="lf-map-wrap">
          <div ref={mapRef} className="lf-map lf-map--editor" />
          {!map && <div className="lf-map-msg">{mapError || 'Loading the map…'}</div>}
        </div>
        <ZonesLayer map={map} items={otherItems} interactive={false} showRoutes={false} />
        {sectors && <SectorCircles map={map} />}
        <ShapeEditor map={map} kind="polygon" points={shape.outline} closed={shape.closed} active={mode === 'outline'}
          colour={OUTLINE_COLOUR} onChange={(outline, closed) => changeShape({ outline, closed })} />
        <ShapeEditor map={map} kind="line" points={shape.route} active={mode === 'route'}
          colour={ROUTE_COLOUR} onChange={route => changeShape({ route })} />
      </div>

      <div className="lf-editor__form ui-card">
        <div className="trade-form">
          <h2 className="lf-editor__title">{adding ? 'New zone' : zone.name}</h2>
          {overlaps.length > 0 && (
            <div className="ui-banner ui-banner--warn">
              Overlaps {overlaps.map(z => z.name).join(', ')}. Fine if they're never given out at the same time.
            </div>
          )}
          <div className="inv-grid2">
            <div><Label>Name</Label><Input value={form.name} onChange={set('name')} placeholder="Hullbridge North" /></div>
            <div><Label>Area</Label><Input value={form.area} onChange={set('area')} placeholder="Hullbridge" /></div>
          </div>

          <div>
            <Label hint="letterboxes, roughly">Homes</Label>
            <div className="trade-row">
              <Input type="number" min="0" step="1" value={form.homes} onChange={set('homes')} style={{ width: 120 }} />
              <Button onClick={findStreets} disabled={!shape.closed || busy === 'survey'}>
                {busy === 'survey' ? 'Asking OpenStreetMap…' : 'Find streets and homes'}
              </Button>
            </div>
            <div className="trade-form__hint">
              {shape.closed && `${areaKm2(shape.outline).toFixed(2)} km². `}
              {busy === 'survey'
                ? 'OpenStreetMap can take a minute or two when it\'s busy. Carry on with the rest meanwhile.'
                : found
                ? found.basis === 'streets'
                  ? `OpenStreetMap has ${found.streets.length} streets here but few buildings mapped, so ${formatCount(found.homes)} homes is a rough guess from ${(found.streetMetres / 1000).toFixed(1)} km of street. Change it if you know better.`
                  : `OpenStreetMap: ${found.streets.length} streets and ${formatCount(found.mappedHomes)} homes mapped. Check it looks right.`
                : form.homesSource === 'osm' && form.surveyedAt
                  ? `From OpenStreetMap, ${formatDay(form.surveyedAt)}.`
                  : 'Find streets fills this in from the houses mapped on OpenStreetMap.'}
            </div>
          </div>

          <div>
            <Label hint="one per line, in walking order">Streets</Label>
            <Textarea rows={8} value={form.streets} onChange={set('streets')} placeholder={'Spa Road\nBramerton Road\n…'} />
            <div className="trade-form__hint">
              {streets.length} street{streets.length === 1 ? '' : 's'}
              {zone && ticked > 0 && (
                <>, {ticked} ticked off. <button type="button" className="lf-link" onClick={clearTicks}>Clear ticks</button> to start the zone again.</>
              )}
            </div>
          </div>

          <div>
            <Label hint="shown in the app and printed">Instructions</Label>
            <Textarea rows={3} value={form.instructions} onChange={set('instructions')} placeholder="e.g. Start at the Ferry Road end. Skip the caravan park." />
          </div>
          <div>
            <Label hint="only in Farm OS">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={set('notes')} />
          </div>
          {!adding && (
            <Checkbox checked={form.archived} onChange={set('archived')} label="Archived (hidden from the map and from new jobs)" />
          )}

          {error && <div className="ui-banner ui-banner--bad">{error}</div>}
        </div>
        <div className="trade-form__actions">
          {!adding && !jobsHere.length && <Button variant="danger" onClick={remove} style={{ marginRight: 'auto' }}>Delete</Button>}
          <Button variant="muted" onClick={close}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : adding ? 'Save zone' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}
